import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import BackgroundImage from '@deliveroo/consumer-component-library/components/BackgroundImage';
import Span from '@deliveroo/consumer-component-library/components/Span';
import P from '@deliveroo/consumer-component-library/components/P';
import i18n from '../../utils/i18n';

import styles from './styles.scss';

/**
 * The receipt view that is rendered in the "view receipt" modal on the order status page
 */
function OrderReceipt({
  orderDetails,
  imageUrl,
  restaurantName,
  placedAtMessage,
  totals,
  totalAmount,
}) {
  /**
   * Filtering out order total from totals array as it
   * is now displayed in different part of the receipt.
   */
  const totalDetails = totals.filter((total) => total.type !== 'total');

  return (
    <div className={styles['order-status-receipt-modal']}>
      <div className={styles.restaurant}>
        <div className={styles['restaurant-image']}>
          {imageUrl && <BackgroundImage src={imageUrl} />}
        </div>
        <div className={styles['restaurant-details']}>
          <Span isBold isBlock>
            {restaurantName}
          </Span>
          <Span isBlock>{placedAtMessage}</Span>
        </div>
      </div>
      <table className={styles.items}>
        <tbody>
          {orderDetails.orderItems.map((item) => (
            <tr key={item.name + item.modifiersDescription} className={styles.item}>
              <td className={styles['item-quantity']}>
                <Span>{item.quantity} x</Span>
              </td>
              <td className={styles['item-name']}>
                <Span>
                  {item.name}
                  {item.modifiersDescription && (
                    <P color="grey" size="small">
                      {item.modifiersDescription}
                    </P>
                  )}
                </Span>
              </td>
              <td className={styles['item-price']}>
                <Span>{item.totalPrice}</Span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalDetails.length > 0 && (
        <table className={styles.totals}>
          <tbody>
            {totalDetails.map((totalRow) => {
              const trClassName = classnames(
                styles['total-row'],
                styles[`total-row--${totalRow.type}`],
              );
              return (
                <tr key={totalRow.label} className={trClassName}>
                  <td className={styles['total-label']}>
                    <Span>{totalRow.label}</Span>
                  </td>
                  <td className={styles['total-amount']}>
                    <Span>{totalRow.amount}</Span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {totalAmount && (
        <table className={styles.totals}>
          <tbody>
            <tr>
              <td className={styles['total-label']}>
                <Span>{i18n.gettext('order_status_page.total')}</Span>
              </td>
              <td className={styles['total-amount']}>
                <Span isBold>{totalAmount}</Span>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

OrderReceipt.propTypes = {
  /** Used as the circluar image for the restuarant */
  imageUrl: PropTypes.string.isRequired,
  /** Contains all the items within the order and their price */
  orderDetails: PropTypes.shape({
    orderItems: PropTypes.arrayOf(
      PropTypes.shape({
        modifiersDescription: PropTypes.string,
        name: PropTypes.string,
        quantity: PropTypes.number,
        totalPrice: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
  /** Order date */
  placedAtMessage: PropTypes.string.isRequired,
  /** Name of the restaurant ordered from */
  restaurantName: PropTypes.string.isRequired,
  /** Total cost of the order */
  totalAmount: PropTypes.string,
  /** An array of 5 objects which has the break down of the costs for the order.
   * The types included: subtotal, surcharge, delivery_fee, rider_tip and total
   */
  totals: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.oneOf([
        'credit',
        'subtotal',
        'surcharge',
        'delivery_fee',
        'rider_tip',
        'total',
      ]),
    }),
  ).isRequired,
};

OrderReceipt.defaultProps = {
  totalAmount: null,
};

export default OrderReceipt;
